import { el, iconBtn } from '../panels.js';

/**
 * The bottom sheet every phone overlay is made of: a grab handle you can drag
 * down to dismiss, a three-slot header, and a scrolling body.
 *
 * It is deliberately not the designer's `.pzd-panel`: that one is the desktop
 * tool card reshaped, while this is the Add drawer, the ⋯ menu, the Pregled
 * sheet and the long-press menu — four things that share a gesture, a scrim
 * and a focus rule and nothing else.
 *
 * Drag-to-dismiss is written with pointer events only (no touch events), so
 * the same gesture works under a mouse in the emulated phone the lead verifies
 * in; the handle is also a button, so a keyboard can dismiss the sheet too.
 */
export interface SheetAction {
  label: string;
  onClick(): void;
  /** Quiet styling for the cancelling side. */
  quiet?: boolean;
}

export interface SheetOptions {
  /** The shell root; the scrim and the sheet mount into it. */
  root: HTMLElement;
  title: string;
  closeLabel: string;
  /** Nearly full height (the Pregled sheet). */
  tall?: boolean;
  /** Browsing a catalogue should not immediately open the on-screen keyboard. */
  initialFocus?: 'control' | 'dialog';
  /** Fills the body; returns a teardown when it holds resources. */
  build(body: HTMLElement, sheet: SheetHandle): void | (() => void);
  onClose?(): void;
}

export interface SheetHandle {
  element: HTMLElement;
  body: HTMLElement;
  /** Above the body, under the header: the Add drawer's tab strip lives here. */
  strip: HTMLElement;
  setTitle(text: string): void;
  /** Replaces the header's side buttons ("OTKAŽI | … | PRIMENI"). */
  setActions(left: SheetAction | null, right: SheetAction | null): void;
  /** Re-runs `build` after the caller changed what it wants to show. */
  rebuild(build: (body: HTMLElement, sheet: SheetHandle) => void | (() => void)): void;
  close(): void;
}

export function openSheet(options: SheetOptions): SheetHandle {
  const scrim = el('button', 'pzd-ph-scrim');
  scrim.type = 'button';
  scrim.setAttribute('aria-label', options.closeLabel);

  const sheet = el('div', `pzd-ph-sheet${options.tall ? ' is-tall' : ''}`);
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'false');
  sheet.setAttribute('aria-label', options.title);
  sheet.tabIndex = -1;

  const handle = el('button', 'pzd-ph-handle');
  handle.type = 'button';
  handle.setAttribute('aria-label', options.closeLabel);

  const head = el('div', 'pzd-ph-sheet-head');
  const leftSlot = el('div');
  const heading = el('h3', undefined, options.title);
  const rightSlot = el('div');
  head.append(leftSlot, heading, rightSlot);

  const strip = el('div', 'pzd-ph-strip');
  strip.hidden = true;
  const body = el('div', 'pzd-panel-body pzd-ph-sheet-body');

  sheet.append(handle, head, strip, body);

  let teardown: (() => void) | undefined;
  let closed = false;

  function close(): void {
    if (closed) return;
    closed = true;
    teardown?.();
    teardown = undefined;
    sheet.remove();
    scrim.remove();
    options.onClose?.();
  }

  const api: SheetHandle = {
    element: sheet,
    body,
    strip,
    setTitle(text) {
      heading.textContent = text;
      sheet.setAttribute('aria-label', text);
    },
    setActions(left, right) {
      leftSlot.textContent = '';
      rightSlot.textContent = '';
      if (left) leftSlot.append(actionButton(left));
      if (right) rightSlot.append(actionButton(right));
      if (!right) rightSlot.append(iconBtn('close', options.closeLabel, close, { className: 'pzd-ph-icon' }));
    },
    rebuild(build) {
      teardown?.();
      teardown = undefined;
      body.textContent = '';
      const result = build(body, api);
      if (typeof result === 'function') teardown = result;
      body.scrollTop = 0;
    },
    close,
  };

  function actionButton(action: SheetAction): HTMLButtonElement {
    const button = el('button', `pzd-ph-text-btn${action.quiet ? ' is-quiet' : ''}`, action.label);
    button.type = 'button';
    button.setAttribute('aria-label', action.label);
    button.addEventListener('click', action.onClick);
    return button;
  }

  api.setActions(null, null);
  scrim.addEventListener('click', close);
  handle.addEventListener('click', close);
  sheet.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key !== 'Escape') return;
    event.stopPropagation();
    close();
  });

  // Drag the handle down to dismiss — the gesture a phone shopper tries first.
  let startY: number | undefined;
  handle.addEventListener('pointerdown', (event) => {
    startY = event.clientY;
    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      /* a mouse without capture support still works through the move events */
    }
  });
  handle.addEventListener('pointermove', (event) => {
    if (startY === undefined) return;
    const dy = event.clientY - startY;
    sheet.style.transform = dy > 0 ? `translateY(${Math.round(dy)}px)` : '';
  });
  const endDrag = (event: PointerEvent): void => {
    if (startY === undefined) return;
    const dy = event.clientY - startY;
    startY = undefined;
    sheet.style.transform = '';
    if (dy > 70) close();
  };
  handle.addEventListener('pointerup', endDrag);
  handle.addEventListener('pointercancel', () => {
    startY = undefined;
    sheet.style.transform = '';
  });

  options.root.append(scrim, sheet);
  api.rebuild(options.build);
  // Focus something inside rather than leaving it on the button that opened a
  // sheet that now covers half the screen.
  window.setTimeout(() => {
    if (closed) return;
    const first = options.initialFocus === 'dialog'
      ? sheet.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]') ?? sheet
      : body.querySelector<HTMLElement>('button, input, textarea, select, [tabindex]');
    (first ?? handle).focus({ preventScroll: true });
  }, 20);

  return api;
}
